<p-stepper [(value)]="currentStep" [linear]="true">
  <p-step-list>
    <p-step [value]="1">Details</p-step>
    <p-step [value]="2">Ownership</p-step>
    <p-step [value]="3">Profiles</p-step>
    <p-step [value]="4">Finish</p-step>
  </p-step-list>

  <p-step-panels>

    <p-step-panel [value]="1">
      <ng-template #content let-activateCallback="activateCallback">
        <p>Submit your details to request your free profile. We'll set it up and reach out to you when it's ready to review.</p>

        <div [formGroup]="submitForm" class="field">
          <div class="field">
            <label for="name">Organisation name</label>
            <input
              [style]="{ width: '100%' }"
              id="name"
              type="text"
              formControlName="name"
              pInputText
            />
            @if (
              submitForm.controls['name'].touched &&
              submitForm.controls['name'].hasError('required')
            ) {
              <div class="error-message">Organisation name is required</div>
            }
            @else if (
              nameExists()
            ) {
              <div class="error-message">Organisation name is already in use</div>
            }
          </div>
          <div class="field">
            <label for="type">Type</label>
            <p-select 
              [style]="{ width: '100%' }"
              id="type"
              formControlName="type"
              [options]="organizationTypes"
              optionLabel="name"
              optionValue="id"
              appendTo="body"
            />
            @if (
              submitForm.controls['type'].touched &&
              submitForm.controls['type'].hasError('required')
            ) {
              <div class="error-message">Type is required</div>
            }
          </div>
          <div class="field">
            <label for="website_url">Website</label>
            <input
              [style]="{ width: '100%' }"
              id="website_url"
              type="text"
              formControlName="website_url"
              pInputText
            />
            @if (
              submitForm.controls['website_url'].touched &&
              submitForm.controls['website_url'].hasError('required')
            ) {
              <div class="error-message">Website is required</div>
            }
            @else if (
              websiteExists()
            ) {
              <div class="error-message">Website is already in use</div>
            }
          </div>
        </div>

        <div style="display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px;">
          <p-button
            label="Next"
            [rounded]="true"
            [disabled]="
              submitForm.controls['name'].invalid || 
              submitForm.controls['type'].invalid || 
              submitForm.controls['website_url'].invalid ||
              nameExists() || 
              websiteExists()
            "
            [loading]="validating()"
            (onClick)="activateCallback(2); checkEmailMatch()"
          />
        </div>
      </ng-template>
    </p-step-panel>
    
    <p-step-panel [value]="2">
      <ng-template #content let-activateCallback="activateCallback">
        @switch (emailMatch()) {
          @case ('checking') {
            <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; padding: 24px;">
              <h5>Checking Email Domain</h5>
              <p>We are checking your email domain to see if it matches your organisation.</p>
              <i class="pi pi-spin pi-spinner-dotted" style="font-size: 2rem"></i>
            </div>
          }
          @case ('matches') {
            
          }
          @case ('mismatches') {
            <div style="text-align: center; margin-bottom: 16px;">
              <span class="material-symbols-outlined">error</span>
            </div>

            <h5>Your email doesn't match the organisation’s domain</h5>
            <p>To continue, we need to confirm you’re connected to the organisation.</p>
            
            <p-divider />

            <h5>Option 1: Start over with your organisation email</h5>
            <p>
              For a faster process, please <a tabindex="0" (click)="logout()" (keydown.space)="logout()" class="rt-clickable" style="text-decoration: underline;">log out</a> 
              and sign up again using your work email (e.g. you&#64;orgname.com).
              <br />
              <span class="color-light" style="display: inline-block; margin-top: 8px;">(Faster — instant verification if email matches)</span>
            </p>

            <h5>Option 2: Keep using this email</h5>
            <p>
              You can continue with this email, and our team will manually review your request.
              <br />
              <span class="color-light" style="display: inline-block; margin-top: 8px;">(Slower — we’ll reach out to confirm your connection)</span>
            </p>
    
            <div style="display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px;">
              <p-button
                label="Continue with this email"
                [rounded]="true"
                (onClick)="activateCallback(3)"
              />
            </div>           
          }
        }
      </ng-template>
    </p-step-panel>

    <p-step-panel [value]="3">
      <ng-template #content let-activateCallback="activateCallback">
        <p>Please provide a link to at least one of your social media profiles. You can add Facebook, or Instagram — only one is required.</p>

        <div [formGroup]="submitForm" class="field">
          <div class="field">
            <label for="facebook_url">Facebook</label>
            <input
              [style]="{ width: '100%' }"
              id="facebook_url"
              type="text"
              formControlName="facebook_url"
              placeholder="https://www.facebook.com/yourprofile/"
              pInputText
            />
            @if (
              submitForm.controls['facebook_url'].touched &&
              submitForm.hasError('socialRequired')
            ) {
              <div class="error-message">At least one social profile is required</div>
            }
            @if (
              submitForm.controls['facebook_url'].touched &&
              submitForm.controls['facebook_url'].hasError('pattern')
            ) {
              <div class="error-message">
                Profile url is not valid.<br>
                Paste the full URL (e.g., https://www.facebook.com/yourprofile/).
              </div>
            }
          </div>
          <div class="field">
            <label for="instagram_url">Instagram</label>
            <input
              [style]="{ width: '100%' }"
              id="instagram_url"
              type="text"
              formControlName="instagram_url"
              placeholder="https://www.instagram.com/yourprofile/"
              pInputText
            />
            @if (
              submitForm.controls['instagram_url'].touched &&
              submitForm.hasError('socialRequired')
            ) {
              <div class="error-message">At least one social profile is required</div>
            }
            @if (
              submitForm.controls['instagram_url'].touched &&
              submitForm.controls['instagram_url'].hasError('pattern')
            ) {
              <div class="error-message">
                Profile url is not valid.<br>
                Paste the full URL (e.g., https://www.instagram.com/yourprofile/).
              </div>
            }
          </div>
        </div>
        
        <div style="display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px;">
          <p-button
            label="Back"
            [rounded]="true"
            severity="secondary"
            (onClick)="activateCallback(1)"
          />
          <p-button
            label="Submit"
            [rounded]="true"
            [loading]="submitting()"
            [disabled]="submitForm.invalid"
            (onClick)="submit()"
          />
        </div>
      </ng-template>
    </p-step-panel>
    
    <p-step-panel [value]="4">
      <ng-template #content let-activateCallback="activateCallback">
        <h4>Submission Profile Success</h4>
        <p>Thank you for submitting your details to claim the organization profile! We'll set it up and reach out to you when it's ready for review. </p>
        <p>Feel free to keep browsing while you wait!</p>
        
        <div style="display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px;">
          <p-button
            label="Close"
            [rounded]="true"
            (onClick)="close()"
          />
        </div>
      </ng-template>
    </p-step-panel>

  </p-step-panels>
</p-stepper>
